'use client';

import InformationCard, { IInformationCard } from '@umodeapporg/ui/components/UI/InformationCard';
import NoDataFound from '@umodeapporg/ui/components/UI/NoDataFound';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { formatCurrency } from '@/app/ops/helpers/formatters';
import { TAB_DIAGNOSTICO, temposHref } from '@/app/tempos/helpers/temposTabs';
import { buildHomeExplicacaoPorNumero } from '@/components/home/homeExplica';
import { OpsSummary } from '@/domain/ops/interfaces/ops';
import { formatNumber } from '@/infrastructure/persistence/managers/helpers';

const CARTEIRA_PATH = '/ops';
/** The Carteira already filtered by the risk, which is the list behind the number. */
const CARTEIRA_RISCO_PATH = '/ops?risco=EM_RISCO';
const FILA_PATH = '/fila';
/** A tela virou aba de Tempos: o link vai direto, sem passar pela redireção. */
const CURADORIA_PATH = temposHref(TAB_DIAGNOSTICO);

interface HomeMetricsProps {
  /** Portfolio numbers as the Carteira reads them. */
  resumo: OpsSummary | null;
  /** Pace deviations as the queue counted them. */
  desvios: number | null;
  loading: boolean;
  /** A source that failed hides ITS numbers: a zero there would read as good news. */
  resumoFailed: boolean;
  filaFailed: boolean;
  onRetry: () => void;
  /** Tolerated deviation in force, as a fraction; `null` until the settings arrive. */
  tolerancia: number | null;
  /** Minimum sample a deviation needs to open a queue item. */
  amostraMinima: number | null;
}

/** The numbers of the day, each one a door to the screen where it gets resolved. */
export function HomeMetrics({
  resumo,
  desvios,
  loading,
  resumoFailed,
  filaFailed,
  onRetry,
  tolerancia,
  amostraMinima,
}: HomeMetricsProps) {
  const { t } = useTranslation('common');
  const router = useRouter();

  // Each number says what it counts, and with which numbers of today, in the (i) beside
  // its own label: the row of chips under the cards cost a line of the first screenful.
  const explicacao = buildHomeExplicacaoPorNumero({ t, resumo, desvios, tolerancia, amostraMinima });

  /**
   * Zero não tem para onde levar: o cartão deixa de ser clicável em vez de abrir uma
   * lista vazia, e com isso perde o cursor e o realce de hover. É o quanto de discrição
   * cabe aqui dentro do contrato do `InformationCard`, que não expõe estilo por cartão.
   */
  const abrirSeTiver = (valor: number | null | undefined, ir: () => void): (() => void) | undefined =>
    (valor ?? 0) > 0 ? ir : undefined;

  // The whole card is the door to the screen that resolves the number, so it needs no
  // link text of its own: the pointer and the hover already say it is clickable.
  const cards: Array<IInformationCard> = [
    // First because it is the one that costs money, and the only one in reais: the
    // others count OPs, this one counts what the factory may not bill on time.
    {
      title: t('home.numeros.riscoFinanceiro'),
      icon: 'crisis_alert',
      value: formatCurrency(resumo?.riscoFinanceiro?.valor ?? 0),
      secondaryValue: t('home.numeros.riscoOps', { count: resumo?.riscoFinanceiro?.ops ?? 0 }),
      tooltip: explicacao.get('riscoFinanceiro'),
      show: !resumoFailed,
      onClick: abrirSeTiver(resumo?.riscoFinanceiro?.ops, () => router.push(CARTEIRA_RISCO_PATH)),
    },
    {
      title: t('home.numeros.entregasVencidas'),
      icon: 'event_busy',
      value: formatNumber(resumo?.entregasVencidas ?? 0),
      tooltip: explicacao.get('entregasVencidas'),
      show: !resumoFailed,
      onClick: abrirSeTiver(resumo?.entregasVencidas, () => router.push(CARTEIRA_PATH)),
    },
    {
      title: t('home.numeros.travadasMateriaPrima'),
      icon: 'block',
      value: formatNumber(resumo?.travadasMateriaPrima ?? 0),
      tooltip: explicacao.get('travadasMateriaPrima'),
      show: !resumoFailed,
      onClick: abrirSeTiver(resumo?.travadasMateriaPrima, () => router.push(CARTEIRA_PATH)),
    },
    {
      title: t('home.numeros.desvios'),
      icon: 'speed',
      value: formatNumber(desvios ?? 0),
      tooltip: explicacao.get('desvios'),
      show: !filaFailed,
      onClick: abrirSeTiver(desvios, () => router.push(FILA_PATH)),
    },
    {
      title: t('home.numeros.semTempoPorPeca'),
      icon: 'timer_off',
      value: formatNumber(resumo?.semTempoPorPeca ?? 0),
      tooltip: explicacao.get('semTempoPorPeca'),
      show: !resumoFailed,
      onClick: abrirSeTiver(resumo?.semTempoPorPeca, () => router.push(CURADORIA_PATH)),
    },
  ];

  const tudoFalhou = resumoFailed && filaFailed;
  const algoFalhou = resumoFailed || filaFailed;

  return (
    <section className="flex flex-col gap-2">
      {!tudoFalhou && <InformationCard cards={cards} loading={loading} showHeader={false} />}

      {/* Partial failure keeps the numbers that did arrive and says the rest is missing. */}
      {algoFalhou && (
        <NoDataFound
          variant="error"
          compact
          title={t('home.erroTitulo')}
          description={t('home.numeros.erroDescricao')}
          action={{ label: t('home.tentar'), onClick: onRetry, icon: 'refresh' }}
        />
      )}
    </section>
  );
}
